import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { notifySubscriptionRequired } from "./subscriptionLock";
import {
  notifyFeatureNotIncluded,
  FEATURE_NOT_INCLUDED_CODE,
} from "./featureLock";
import {
  notifyWebPlatformRequired,
  WEB_PLATFORM_REQUIRED_CODE,
} from "./webPlatformLock";

export function isApiRoute(route: string): boolean {
  // Pfad aus absoluter oder relativer URL extrahieren und auf /api prüfen.
  try {
    const path = new URL(route, window.location.origin).pathname;
    return path.startsWith("/api/") || path === "/api";
  } catch {
    return route.startsWith("/api/") || route === "/api";
  }
}

export async function throwIfResNotOk(res: Response, route: string) {
  if (!res.ok) {
    let msg = res.statusText;
    let code: string | undefined;
    let feature: string | undefined;
    let retryAfter: number | undefined;
    try {
      const data = await res.json();
      msg = data.error || data.message || msg;
      code = data.code;
      if (typeof data.feature === "string") feature = data.feature;
      if (typeof data.retryAfter === "number") retryAfter = data.retryAfter;
    } catch {
      try {
        msg = (await res.text()) || msg;
      } catch {}
    }
    // Fallback auf den Retry-After-Header, falls das JSON-Feld fehlt.
    if (retryAfter === undefined) {
      const header = res.headers.get("Retry-After");
      const parsed = header ? Number(header) : NaN;
      if (Number.isFinite(parsed)) retryAfter = parsed;
    }
    // Fehlendes Abo: nur für /api-Aufrufe zentral abfangen und freundliche
    // Meldung + Weiterleitung zur Paketauswahl auslösen.
    if (
      isApiRoute(route) &&
      (res.status === 402 || code === "subscription_required")
    ) {
      notifySubscriptionRequired();
    }
    // Feinkörnige Feature-Sperre: aktiver Tarif, aber die ausgelöste Advanced-
    // only-Aktion ist nicht enthalten (z. B. PDF-Export/Fluglog/Dokumente).
    // Freundlichen Upgrade-Hinweis anstelle eines rohen 403 anzeigen.
    if (
      isApiRoute(route) &&
      res.status === 403 &&
      code === FEATURE_NOT_INCLUDED_CODE &&
      feature
    ) {
      notifyFeatureNotIncluded(feature);
    }
    // Web-Plattform-Gate: aktiver Tarif ohne Browser-Zugang. Eine streunende 403
    // aus einer Hintergrund-Query soll denselben freundlichen Upgrade-Hinweis
    // auslösen wie die vollflächige SubscriptionGate-Sperre — kein roher Fehler.
    if (
      isApiRoute(route) &&
      res.status === 403 &&
      code === WEB_PLATFORM_REQUIRED_CODE
    ) {
      notifyWebPlatformRequired();
    }
    const err = new Error(msg) as Error & {
      status?: number;
      code?: string;
      retryAfter?: number;
    };
    err.status = res.status;
    if (code) err.code = code;
    if (retryAfter !== undefined) err.retryAfter = retryAfter;
    throw err;
  }
}

// Kennzeichnet jede Anfrage als Web-Client. Der Server koppelt den Zugang zur
// Web-Plattform an das Feature `web_platform` und antwortet bei fehlender
// Berechtigung mit 403/`web_platform_required` (siehe server/routes.ts).
const WEB_CLIENT_HEADER = { "X-Client-Platform": "web" } as const;

export async function apiRequest(
  method: string,
  route: string,
  data?: unknown
): Promise<Response> {
  const res = await fetch(route, {
    method,
    headers: data
      ? { "Content-Type": "application/json", ...WEB_CLIENT_HEADER }
      : { ...WEB_CLIENT_HEADER },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
  await throwIfResNotOk(res, route);
  return res;
}

export async function apiJson<T = unknown>(
  method: string,
  route: string,
  data?: unknown
): Promise<T> {
  const res = await apiRequest(method, route, data);
  return res.json();
}

const defaultFetcher: QueryFunction = async ({ queryKey }) => {
  const path = queryKey.join("/") as string;
  const res = await fetch(path, {
    credentials: "include",
    headers: { ...WEB_CLIENT_HEADER },
  });
  await throwIfResNotOk(res, path);
  return res.json();
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultFetcher,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 1;
      },
    },
    mutations: { retry: false },
  },
});
